import React from 'react';
import { parsePhoneNumber } from 'libphonenumber-js';
import { Box, Button, ButtonGroup, FormField, FormHelp, Input, Label, Text } from '@zillow/constellation-10';
import UnitedStatesSelect from '@common/components/UnitedStatesSelect';
import { formatPhoneNumber } from '@common/utils/numbersUtils';

import PaymentMethod from '@common/constants/PaymentMethod';
import validateAllInputs from './validation';
import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import { logError } from '@zg-rentals/log-error';

interface Props {
  isLoading?: boolean;
  address: {
    address1: string;
    address2: string;
    state: string;
    city: string;
    zipCode: string;
  };
  modeOfPayment: string;
  onBack: (...args: any) => any;
  onSubmit: (...args: any) => any;
  trackValidationError?: (...args: any) => any;
  // storybook
  skipFieldValidationForStorybook?: boolean;
}

interface State {
  address1: {
    value: string;
    error: string;
  };
  address2: {
    value: string;
    error: string;
  };
  state: {
    value: string;
    error: string;
  };
  city: {
    value: string;
    error: string;
  };
  zipCode: {
    value: string;
    error: string;
  };
  phoneNumber: {
    value: string;
    error: string;
  };
  isLoading?: boolean;
}

class BillingAddressSection extends React.Component<Props, State> {
  static defaultProps = {
    address: {
      address1: '',
      address2: '',
      state: '',
      city: '',
      zipCode: '',
    },
    trackValidationError: () => {},
  };

  constructor(props: Props) {
    super(props);
    const { address1, address2, state, city, zipCode } = props.address;
    this.state = {
      address1: {
        value: address1,
        error: '',
      },
      address2: {
        value: address2,
        error: '',
      },
      state: {
        value: state,
        error: '',
      },
      city: {
        value: city,
        error: '',
      },
      zipCode: {
        value: zipCode,
        error: '',
      },
      phoneNumber: {
        value: '',
        error: '',
      },
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
  }

  storeAddressInState = (value, key) => {
    // @ts-expect-error  will  be addressed in RJS upgrade
    this.setState({
      [key]: {
        value: value,
        error: '',
      },
    });
  };

  storePhoneNumberInState = (e: any) => {
    const phoneNumber = e.currentTarget.value.replace(/[^\d]/g, '').slice(0, 10);
    this.setState({
      phoneNumber: {
        value: phoneNumber,
        error: '',
      },
    });
  };

  onSubmit = async () => {
    if (this.props.skipFieldValidationForStorybook) {
      this.props.onSubmit({});
    }
    try {
      if (!validateAllInputs(this)) {
        return false;
      }

      await this.setState({ isLoading: true });

      const payload = {
        address: {
          address1: this.state.address1.value,
          address2: this.state.address2.value,
          state: this.state.state.value,
          city: this.state.city.value,
          zipCode: this.state.zipCode.value,
          // Parse saved phone number into the format expected by the backend: eg. "+11234567890"
          phoneNumber: parsePhoneNumber(this.state.phoneNumber.value, 'US').number,
        },
      };

      const response = await this.props.onSubmit(payload);

      if (response && response.error) {
        return this.setState({ isLoading: false });
      }
      return true;
    } catch (err) {
      logError({ error: err, context: 'Error submitting billing address info.' });
      return this.setState({ isLoading: false });
    }
  };

  render() {
    return (
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'token(spacing.sm)' }}>
        {this.props.modeOfPayment === PaymentMethod.TYPE.BANK_ACCOUNT && (
          <Text>Please provide a few details to help confirm your identity. You’ll only have to do this once.</Text>
        )}
        <FormField
          control={
            <Input
              onChange={(e) => this.storeAddressInState(e.target.value, 'address1')}
              value={this.state.address1.value}
              error={Boolean(this.state.address1.error)}
            />
          }
          label={<Label>Street address</Label>}
          description={this.state.address1.error && <FormHelp error>{this.state.address1.error}</FormHelp>}
        />

        <FormField
          control={
            <Input
              onChange={(e) => this.storeAddressInState(e.target.value, 'address2')}
              value={this.state.address2.value}
              error={Boolean(this.state.address2.error)}
            />
          }
          label={<Label>Address line 2</Label>}
          description={this.state.address2.error && <FormHelp error>{this.state.address2.error}</FormHelp>}
        />

        <FormField
          control={
            <Input
              onChange={(e) => this.storeAddressInState(e.target.value, 'city')}
              value={this.state.city.value}
              error={Boolean(this.state.city.error)}
            />
          }
          label={<Label>City</Label>}
          description={this.state.city.error && <FormHelp error>{this.state.city.error}</FormHelp>}
        />

        <FormField
          control={
            <UnitedStatesSelect
              onChange={(e) => this.storeAddressInState(e.target.value, 'state')}
              value={this.state.state.value}
              error={Boolean(this.state.state.error)}
            />
          }
          label={<Label>State</Label>}
          description={this.state.state.error && <FormHelp error>{this.state.state.error}</FormHelp>}
        />
        <FormField
          control={
            <Input
              onChange={(e) => this.storeAddressInState(e.target.value, 'zipCode')}
              value={this.state.zipCode.value}
              error={Boolean(this.state.zipCode.error)}
            />
          }
          label={<Label>Zip</Label>}
          description={this.state.zipCode.error && <FormHelp error>{this.state.zipCode.error}</FormHelp>}
        />
        <FormField
          control={
            <Input
              data-id="phoneNumber"
              onChange={this.storePhoneNumberInState}
              value={formatPhoneNumber(this.state.phoneNumber.value)}
              error={Boolean(this.state.phoneNumber.error)}
              type="tel"
            />
          }
          label={<Label>Phone</Label>}
          description={this.state.phoneNumber.error && <FormHelp error>{this.state.phoneNumber.error}</FormHelp>}
        />
        <ButtonGroup aria-label="controls" role="group">
          <Button size="lg" fluid={true} onClick={this.props.onBack}>
            Back
          </Button>
          <DebouncedButton
            emphasis="filled"
            size="lg"
            fluid={true}
            onClick={this.onSubmit}
            loading={this.props.isLoading}
          >
            Continue
          </DebouncedButton>
        </ButtonGroup>
      </Box>
    );
  }
}

export default BillingAddressSection;
